import { DataTableFilterMeta } from "primevue";
import { FilterMatchMode } from "@primevue/core/api";
import { isCustomFieldFilterKey } from "@/utils/customFieldFilters";

export const ORDERS_TABLE_FILTERS_KEY = "ordersTableFilters";
export const UNASSIGNED_JOBS_FILTERS_KEY = "unassignedJobsTableFilters";
export const PRINTER_JOBS_FILTERS_KEY = "printerJobsTableFilters";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;

export function isFilterValueActive(filterModel: any): boolean {
  const value = filterModel?.value;
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) {
    if (String(filterModel?.matchMode ?? "").toLowerCase() === "between" && value.length === 2) {
      const [from, to] = value;
      return Boolean(from || to);
    }
    return value.length > 0;
  }
  if (typeof value === "string") return value.trim().length > 0;
  return true;
}

export function hasActiveTableFilters(filters: DataTableFilterMeta): boolean {
  return Object.values(filters).some(isFilterValueActive);
}

function reviveFilterValue(value: unknown): unknown {
  if (typeof value === "string" && ISO_DATE.test(value)) return new Date(value);
  if (Array.isArray(value)) return value.map(reviveFilterValue);
  if (value && typeof value === "object") {
    const next: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
      next[key] = reviveFilterValue(nested);
    }
    return next;
  }
  return value;
}

function toFilterMetaObject(value: unknown): Record<string, unknown> {
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

export function mergeFilterMeta(defaults: DataTableFilterMeta, stored: DataTableFilterMeta): DataTableFilterMeta {
  const next: DataTableFilterMeta = { ...defaults };
  for (const [key, meta] of Object.entries(stored)) {
    const merged = Object.assign({}, toFilterMetaObject(defaults[key]), toFilterMetaObject(meta)) as any;
    if ((defaults[key] as any)?.matchMode === FilterMatchMode.IN) {
      merged.matchMode = FilterMatchMode.IN;
      if (typeof merged.value === "string" && merged.value) {
        merged.value = [merged.value];
      }
    }
    next[key] = merged as unknown as DataTableFilterMeta[string];
  }
  return next;
}

function getSessionStorage(): Storage | null {
  try {
    if (typeof sessionStorage === "undefined") return null;
    return sessionStorage;
  } catch {
    return null;
  }
}

export function loadTableFilters(key: string, defaults: DataTableFilterMeta): DataTableFilterMeta {
  try {
    const raw = getSessionStorage()?.getItem(key);
    if (!raw) return defaults;
    const stored = reviveFilterValue(JSON.parse(raw)) as DataTableFilterMeta;
    if (!stored || typeof stored !== "object" || Array.isArray(stored)) return defaults;
    return mergeFilterMeta(defaults, stored);
  } catch {
    return defaults;
  }
}

export function saveTableFilters(key: string, filters: DataTableFilterMeta) {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    if (!hasActiveTableFilters(filters)) {
      storage.removeItem(key);
      return;
    }
    storage.setItem(key, JSON.stringify(filters));
  } catch {
    // Ignore quota / private-mode failures
  }
}

export function clearTableFilters(key: string) {
  try {
    getSessionStorage()?.removeItem(key);
  } catch {
    // ignore
  }
}

export function routeHasFilterQuery(query: Record<string, any>, filterKeys: string[]): boolean {
  const keySet = new Set(filterKeys);
  return Object.keys(query).some((key) => {
    const base = key.split("__")[0];
    return keySet.has(key) || keySet.has(base) || isCustomFieldFilterKey(base);
  });
}
